import React, { useCallback, useEffect, useState } from "react";
import { GetRequest } from "../../../api/Request";
import { CampaignEntry, CampaignEnum } from "./campaign.types";

const ENTRY_PAGE_LIMIT = 35;

/** Times are shown in India time whatever the admin's computer is set to. */
const formatIndiaTime = (value?: string) =>
  value
    ? new Date(value).toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        weekday: "short",
        day: "2-digit",
        month: "short",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
    : "-";

/** "businessName" -> "Business name" */
const toLabel = (key: string) =>
  key.charAt(0).toUpperCase() +
  key
    .slice(1)
    .replace(/([A-Z])/g, " $1")
    .toLowerCase();

const toText = (value: unknown) =>
  value === true ? "Yes" : value === false ? "No" : String(value ?? "-");

interface CampaignEntryTableProps {
  campaign: CampaignEnum;
  entryCount: number;
  /** Changes whenever the page header's Refresh is pressed. */
  refreshKey: number;
}

/**
 * Entries of any campaign, newest first. Every key the campaign page saved in `detail`
 * becomes a column, so a new campaign needs no admin change.
 */
const CampaignEntryTable: React.FC<CampaignEntryTableProps> = ({
  campaign,
  entryCount,
  refreshKey,
}) => {
  const [entryList, setEntryList] = useState<CampaignEntry[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEntryList = useCallback(
    async (pageNumber: number) => {
      try {
        setLoading(true);
        setError(null);
        const response = await GetRequest(
          `admin/v1/campaign/${campaign}/entry?page=${pageNumber}&limit=${ENTRY_PAGE_LIMIT}`,
        );
        setEntryList(response.data || []);
      } catch (err: any) {
        setError(err?.message || "Failed to fetch entries");
      } finally {
        setLoading(false);
      }
    },
    [campaign],
  );

  useEffect(() => {
    fetchEntryList(page);
  }, [fetchEntryList, page, refreshKey]);

  const detailKeyList = Array.from(
    new Set(entryList.flatMap((entry) => Object.keys(entry.detail || {}))),
  );
  const hasSlot = entryList.some((entry) => entry.slot);

  return (
    <>
      <div className="campaign-dashboard__stats">
        <div className="campaign-dashboard__stat-card campaign-dashboard__stat-card--primary">
          <div className="stat-label">Entries</div>
          <div className="stat-value">
            {Number(entryCount || 0).toLocaleString("en-IN")}
          </div>
          <div className="stat-subtitle">All time</div>
        </div>
      </div>

      <div className="campaign-dashboard__panel">
        <h2>Entries</h2>
        {error ? (
          <div className="campaign-dashboard__error">{error}</div>
        ) : loading && entryList.length === 0 ? (
          <div className="no-data">Loading entries...</div>
        ) : entryList.length === 0 ? (
          <div className="no-data">
            {page > 1 ? "No more entries." : "No entries yet."}
          </div>
        ) : (
          <div className="table-scroll">
            <table className="campaign-dashboard__table">
              <thead>
                <tr>
                  <th>Entry No.</th>
                  {hasSlot && <th>Booked time</th>}
                  <th>Name</th>
                  <th>Phone</th>
                  {detailKeyList.map((key) => (
                    <th key={key}>{toLabel(key)}</th>
                  ))}
                  <th>Entered at</th>
                </tr>
              </thead>
              <tbody>
                {entryList.map((entry) => (
                  <tr key={entry._id || entry.entryNumber}>
                    <td>
                      <b>{entry.entryNumber}</b>
                    </td>
                    {hasSlot && (
                      <td>
                        <b>{formatIndiaTime(entry.slot)}</b>
                      </td>
                    )}
                    <td>{entry.name}</td>
                    <td>
                      {entry.phoneNumber ? (
                        <a
                          href={`tel:+${entry.phoneNumber.replace(/\D/g, "")}`}
                        >
                          {entry.phoneNumber}
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                    {detailKeyList.map((key) => (
                      <td key={key}>{toText(entry.detail?.[key])}</td>
                    ))}
                    <td>{formatIndiaTime(entry.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="campaign-dashboard__pagination">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
          >
            Previous
          </button>
          <span>Page {page}</span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={entryList.length < ENTRY_PAGE_LIMIT || loading}
          >
            Next
          </button>
        </div>
      </div>
    </>
  );
};

export default CampaignEntryTable;
